import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Alert,
  Platform,
  TextInput,
  Image,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  getTravelDetail,
  clearCurrentTravel,
  getTravelTodos,
} from '@cubemeet/core/store/slices/travelsSlice';
import { fetchGroupReceipts, fetchGroupPrivateReceipts } from '@cubemeet/core/store/slices/expenseReceiptsSlice';
import { approveReceipt } from '@cubemeet/core/store/slices/expenseReceiptsSlice';
import { createGroupTodo, updateGroupTodo } from '@cubemeet/core/store/slices/groupTodosSlice';
import { supabase } from '@cubemeet/core/services/supabase';
import {
  ModuleHeader, 
  TravelCard, 
  Avatar, 
  TodoManagement, 
  PillButton, 
  ProjectDescription,
  LedgerTabs,
  ExpenseStatisticsCard,
  MemberBalanceList,
  PrivateSettlementList,
  ExpenseCategoryChart,
  AddTodoModal,
  UploadReceiptButton,
  Card,
  type LedgerTabType,
  type ExpenseStatistics,
  type MemberBalance,
  type PrivateSettlement,
  type CategoryStat,
} from '@cubemeet/ui';
import ChatPillWeb from './components/ChatPillWeb';
// use library PrivateSettlementList for correct translations/icons
import ReceiptSectionWeb from './components/ReceiptSectionWeb';
import { 
  formatCurrencyCompact,
  EXPENSE_CATEGORIES,
  type ExpenseCategoryKey,
} from '@cubemeet/core/modules';
import { useTravelI18n } from './useTravelI18n';
import { useNavigate } from 'react-router-dom';
import { MODULE_COLORS } from '@cubemeet/core/theme';

interface TravelDetailProps {
  groupId: string;
  onBack: () => void;
  // optional callbacks provided by parent module for navigation
  onOpenInvite?: (groupName?: string | null) => void;
  onOpenExpenseSettings?: () => void;
  chatLeftImage: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
}

const TravelDetail: React.FC<TravelDetailProps> = ({ groupId, onBack, onOpenInvite, onOpenExpenseSettings }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTravelI18n();
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const travelState = useSelector((state: RootState) => (state as any)?.travels ?? {}) as any;
  
  // 创建费用管理组件的翻译函数包装器
  const tExpense = useCallback((key: string, params?: Record<string, any>) => {
    return t(`expenseManagement.${key}`, params) || key;
  }, [t]);
  
  const { user } = authState;
  const { currentTravel, loading, error, currentTravelTodos = [] } = travelState;

  // Local copy of todos for optimistic UI updates on web
  const [localTodos, setLocalTodos] = useState<any[]>([]);

  useEffect(() => {
    setLocalTodos(Array.isArray(currentTravelTodos) ? currentTravelTodos : []);
  }, [currentTravelTodos]);

  // receipts from expenseReceipts slice
  const receipts = useSelector((state: RootState) => (state as any).expenseReceipts?.receipts || []) as any[];

  // private receipts (per-user) and flags (matching mobile slice)
  const privateReceipts = useSelector((state: RootState) => (state as any).expenseReceipts?.privateReceipts || []) as any[];
  const privateReceiptsLoading = useSelector((state: RootState) => (state as any).expenseReceipts?.privateReceiptsLoading ?? false) as boolean;
  const privateReceiptsError = useSelector((state: RootState) => (state as any).expenseReceipts?.privateReceiptsError ?? null) as any;
  const privateReceiptsFetchedGroupId = useSelector((state: RootState) => (state as any).expenseReceipts?.privateReceiptsFetchedGroupId) as string | undefined;

  // Debug: log receipts when they change so we can see whether they were fetched
  useEffect(() => {
    try {
      console.log('[TravelDetail] receipts changed, count=', receipts?.length, receipts?.slice?.(0,5));
    } catch (e) {
      // ignore
    }
  }, [receipts]);

  const [refreshing, setRefreshing] = useState(false);
  const [activeLedgerTab, setActiveLedgerTab] = useState<LedgerTabType>('public');
  const [isExporting, setIsExporting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingReceiptId, setRejectingReceiptId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [showAddTodoModal, setShowAddTodoModal] = useState(false);
  const [isAddingTodo, setIsAddingTodo] = useState(false);

  // Debug: log when AddTodo modal state changes
  useEffect(() => {
    console.log('[TravelDetail] showAddTodoModal state changed ->', showAddTodoModal);
  }, [showAddTodoModal]);

  // 计算用户角色
  const isCreator = useMemo(() => {
    return currentTravel && user ? currentTravel.creator_id === user.id : false;
  }, [currentTravel, user]);

  const isMember = useMemo(() => {
    return (
      currentTravel &&
      user &&
      currentTravel.members?.some((member) => member.user_id === user.id)
    );
  }, [currentTravel, user]);

  // 处理行程描述
  const displayItinerary = useMemo(() => {
    if (!currentTravel) return '';
    
    // 优先显示 itinerary_json 的描述
    if (currentTravel.itinerary_json) {
      try {
        const itinerary = JSON.parse(currentTravel.itinerary_json);
        if (itinerary.description) {
          return itinerary.description;
        }
      } catch (e) {
        // JSON 解析失败，继续使用其他方式
      }
    }
    
    // 回退到基础描述
    return currentTravel.description || '';
  }, [currentTravel]);

  // 加载旅行详情
  const loadTravelDetail = useCallback(async () => {
    if (!groupId) return;

    try {
      await dispatch(getTravelDetail(groupId)).unwrap();
      // Load todos for this travel as well
      try {
        await dispatch(getTravelTodos(groupId)).unwrap();
      } catch (e) {
        console.warn('Failed to load travel todos:', e);
      }
      // no-op: likes removed from web travel detail
    } catch (error: any) {
      console.error('Failed to load travel detail:', error);
      if (typeof window !== 'undefined') {
        Alert.alert('错误', '加载旅行详情失败，请重试');
      }
    }
  }, [dispatch, groupId]);

  // 初始加载
  useEffect(() => {
    loadTravelDetail();
    // also fetch receipts for this group so the Receipts card can render
    if (groupId) {
      dispatch(fetchGroupReceipts(groupId));
      // also prefetch private receipts so tab counts are available immediately
      if (privateReceiptsFetchedGroupId !== groupId) {
        dispatch(fetchGroupPrivateReceipts(groupId));
      }
    }
    
    // 清理函数
    return () => {
      dispatch(clearCurrentTravel());
    };
  }, [loadTravelDetail, dispatch]);

  // 下拉刷新
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadTravelDetail();
    setRefreshing(false);
  }, [loadTravelDetail]);

  // likes removed from this page (no-op)

  // 聊天功能
  const handleChatPress = useCallback(() => {
    if (!currentTravel) return;
    // Use React Router to navigate to the chat route with query params
    try {
      const chatPath = `/chat?groupId=${currentTravel.id}&groupName=${encodeURIComponent(currentTravel.name)}`;
      navigate(chatPath);
    } catch (e) {
      // Fallback to legacy hash navigation for non-router contexts
      if (typeof window !== 'undefined') {
        const chatUrl = `#chat?groupId=${currentTravel.id}&groupName=${encodeURIComponent(currentTravel.name)}`;
        window.location.hash = chatUrl;
      }
    }
  }, [currentTravel]);

  // 邀请好友
  const handleInviteFriends = useCallback(() => {
    if (!currentTravel || !user) return;

    const isPublic = currentTravel.visibility === 'public';
    const canInvite = isCreator || (isPublic && isMember);

    if (!canInvite) {
      const message = isPublic
        ? '您需要先加入项目才能邀请好友'
        : '只有项目创建者可以邀请好友';
      if (typeof window !== 'undefined') {
        Alert.alert('权限不足', message);
      }
      return;
    }

    if (typeof onOpenInvite === 'function') {
      try {
        onOpenInvite(currentTravel.name);
        return;
      } catch (e) {
        // ignore and fallback
      }
    }

    if (typeof window !== 'undefined') {
      Alert.alert('提示', '邀请好友功能请在移动端使用');
    }
  }, [currentTravel, user, isCreator, isMember, onOpenInvite]);

  // 编辑行程
  const handleEditItinerary = useCallback(() => {
    // On web navigate to the editor view; mobile still shows hint
    if (typeof window !== 'undefined' && Platform.OS === 'web') {
      window.location.hash = `#travel?view=editor&groupId=${groupId}`;
      return;
    }
    Alert.alert('提示', '行程编辑功能请在移动端使用');
  }, [groupId]);

  // ========== 费用管理相关 ==========
  
  // 货币格式化函数
  const formatCurrency = useCallback((amount: number, currency?: string) => {
    const curr = currency || currentTravel?.currency || 'HKD';
    return `${curr === 'HKD' ? 'HK$' : curr === 'CNY' ? '¥' : '$'}${amount.toFixed(2)}`;
  }, [currentTravel]);

  // 模拟费用统计数据（TODO: 接入真实数据）
  // Compute public/private statistics based on receipts (port logic from mobile ExpenseManagement)
  const resolveReceiptAmount = useCallback((receipt: any) => {
    if (!receipt) return 0;
    const receiptCurrency = receipt.currency;
    const baseCurrency = currentTravel?.currency;
    const converted = receipt.converted_amount;
    const parsedConverted = converted != null ? Number(converted) : NaN;

    if (
      converted != null &&
      !Number.isNaN(parsedConverted) &&
      receiptCurrency &&
      baseCurrency &&
      receiptCurrency !== baseCurrency
    ) {
      return parsedConverted;
    }

    const parsedAmount = Number(receipt.amount ?? 0);
    return Number.isNaN(parsedAmount) ? 0 : parsedAmount;
  }, [currentTravel]);

  const resolveUploaderId = useCallback((receipt: any) => {
    if (!receipt) return null;
    return (receipt.uploader && receipt.uploader.id) || receipt.uploader_id || receipt.created_by || receipt.payer_id || null;
  }, []);

  const receiptInvolvesUser = useCallback((receipt: any, userId: string | null | undefined) => {
    if (!receipt || !userId) return false;
    if (resolveUploaderId(receipt) === userId) return true;
    const members = (receipt as any).members;
    if (Array.isArray(members) && members.length > 0) {
      return members.some((m: any) => m?.user_id === userId);
    }
    return false;
  }, [resolveUploaderId]);

  // fetch private receipts when switching to private tab
  useEffect(() => {
    if (activeLedgerTab === 'private') {
      if (privateReceiptsFetchedGroupId !== groupId && !privateReceiptsLoading) {
        dispatch(fetchGroupPrivateReceipts(groupId));
      }
    }
  }, [activeLedgerTab, privateReceiptsFetchedGroupId, groupId, privateReceiptsLoading, dispatch]);

  const privateReceiptsForUser = useMemo(() => {
    if (!user?.id) return privateReceipts;
    return privateReceipts.filter(r => receiptInvolvesUser(r, user.id));
  }, [privateReceipts, user, receiptInvolvesUser]);

  const isPrivateTab = activeLedgerTab === 'private';
  const activeReceipts = isPrivateTab ? privateReceiptsForUser : receipts;

  const publicStatistics: ExpenseStatistics = useMemo(() => {
    const approvedReceipts = receipts.filter(r => r.status === 'approved');
    const pendingReceipts = receipts.filter(r => r.status === 'pending');
    const totalApproved = approvedReceipts.reduce((s, r) => s + resolveReceiptAmount(r), 0);
    const totalPending = pendingReceipts.reduce((s, r) => s + resolveReceiptAmount(r), 0);
    const approvedCount = approvedReceipts.length;
    const pendingCount = pendingReceipts.length;
    const memberCount = currentTravel?.members?.length || 1;
    const averagePerPerson = memberCount > 0 ? totalApproved / memberCount : 0;

    // compute simple member balances similar to previous mock but using receipts
    const memberBalancesRes: MemberBalance[] = (currentTravel?.members || []).map((member: any) => {
      const spent = approvedReceipts.filter(r => resolveUploaderId(r) === member.user_id).reduce((s, r) => s + resolveReceiptAmount(r), 0);
      const shouldPay = memberCount > 0 ? totalApproved / memberCount : 0;
      return {
        userId: member.user_id,
        displayName: member.profile?.display_name || '未知用户',
        avatarUrl: member.profile?.avatar_url || null,
        spent,
        shouldPay,
        balance: spent - shouldPay,
        share: null,
      } as MemberBalance;
    });

    return {
      totalApproved,
      totalPending,
      approvedCount,
      pendingCount,
      averagePerPerson,
      memberBalances: memberBalancesRes,
      settlements: [],
    } as any;
  }, [receipts, currentTravel, resolveReceiptAmount, resolveUploaderId]);

  const privateStatistics: ExpenseStatistics = useMemo(() => {
    // ported (simplified) private stats from mobile
    const travelMembers = currentTravel?.members || [];
    const approvedReceipts = privateReceiptsForUser.filter((r: any) => !r.status || r.status === 'approved');
    const pendingReceipts = privateReceiptsForUser.filter((r: any) => r.status === 'pending');
    const totalApproved = approvedReceipts.reduce((s: number, r: any) => s + resolveReceiptAmount(r), 0);
    const totalPending = pendingReceipts.reduce((s: number, r: any) => s + resolveReceiptAmount(r), 0);

    // build member map and settlements similar to mobile
    const profileLookup = new Map<string, { displayName: string | null; avatarUrl: string | null }>();
    travelMembers.forEach((m: any) => profileLookup.set(m.user_id, { displayName: m.profile?.display_name || null, avatarUrl: m.profile?.avatar_url || null }));

    type LB = { userId: string; displayName: string; avatarUrl: string | null; spent: number; shouldPay: number; balance: number; share: number | null };
    const memberMap = new Map<string, LB>();
    const settlementMap = new Map<string, any>();
    const ensureMemberEntry = (userId: string | null, displayName?: string | null, avatarUrl?: string | null) => {
      if (!userId) return null;
      if (!memberMap.has(userId)) {
        const profile = profileLookup.get(userId) || { displayName: '未知用户', avatarUrl: null };
        memberMap.set(userId, { userId, displayName: displayName || profile.displayName || '未知用户', avatarUrl: avatarUrl || profile.avatarUrl || null, spent: 0, shouldPay: 0, balance: 0, share: null });
      }
      return memberMap.get(userId)!;
    };

    const upsertSettlement = (fromUserId: string | null | undefined, toUserId: string | null | undefined, amount: number) => {
      if (!fromUserId || !toUserId || amount <= 0) return;
      const forwardKey = `${fromUserId}->${toUserId}`;
      const reverseKey = `${toUserId}->${fromUserId}`;

      const fromProfile = profileLookup.get(fromUserId) || { displayName: '未知用户', avatarUrl: null };
      const toProfile = profileLookup.get(toUserId) || { displayName: '未知用户', avatarUrl: null };

      const reverse = settlementMap.get(reverseKey);
      if (reverse) {
        const remaining = reverse.amount - amount;
        if (Math.abs(remaining) <= 0.01) { settlementMap.delete(reverseKey); return; }
        if (remaining > 0) { reverse.amount = remaining; return; }
        // reverse amount smaller than incoming -> delete reverse and create/merge forward with net
        settlementMap.delete(reverseKey);
        const net = Math.abs(remaining);
        const ex = settlementMap.get(forwardKey);
        if (ex) {
          ex.amount += net;
        } else {
          settlementMap.set(forwardKey, {
            fromUserId,
            toUserId,
            amount: net,
            fromName: fromProfile.displayName,
            toName: toProfile.displayName,
            fromAvatarUrl: fromProfile.avatarUrl,
            toAvatarUrl: toProfile.avatarUrl,
          });
        }
        return;
      }

      const existing = settlementMap.get(forwardKey);
      if (existing) {
        existing.amount += amount;
        return;
      }

      settlementMap.set(forwardKey, {
        fromUserId,
        toUserId,
        amount,
        fromName: fromProfile.displayName,
        toName: toProfile.displayName,
        fromAvatarUrl: fromProfile.avatarUrl,
        toAvatarUrl: toProfile.avatarUrl,
      });
    };

    approvedReceipts.forEach((receipt: any) => {
      const convertedAmount = resolveReceiptAmount(receipt);
      const uploaderId = resolveUploaderId(receipt);
      const uploaderEntry = ensureMemberEntry(uploaderId, receipt.uploader?.display_name ?? null, receipt.uploader?.avatar_url ?? null);
      if (uploaderEntry) { uploaderEntry.spent += convertedAmount; }

      const participants = (receipt.members || []).filter((p: any) => p && p.user_id);
      if (participants.length === 0) return;
      const rawAmount = Number(receipt.amount || 0);
      const conversionRatio = rawAmount > 0 ? (convertedAmount / rawAmount) : 1;
      const totalShares = participants.reduce((s: number, p: any) => s + Number(p.share || 0), 0);

      participants.forEach((participant: any) => {
        const entry = ensureMemberEntry(participant.user_id, participant.profile?.display_name ?? null, participant.profile?.avatar_url ?? null);
        if (!entry) return;
        let shareAmount = 0;
        switch (participant.split_mode || receipt.split_mode) {
          case 'percentage': shareAmount = convertedAmount * (Number(participant.percentage || 0) / 100); break;
          case 'exact': shareAmount = Number(participant.amount || 0) * conversionRatio; break;
          case 'shares': if (totalShares > 0 && Number(participant.share || 0) > 0) shareAmount = convertedAmount * (Number(participant.share || 0) / totalShares); else shareAmount = convertedAmount / participants.length; break;
          case 'equal':
          default: shareAmount = convertedAmount / participants.length; break;
        }
        if (shareAmount <= 0) return;
        entry.shouldPay += shareAmount;
        if (participant.user_id !== uploaderId && uploaderId) {
          upsertSettlement(participant.user_id, uploaderId, shareAmount);
        }
      });
    });

    const orderedBalances: any[] = [];
    const processed = new Set<string>();
    (currentTravel?.members || []).forEach((m: any) => {
      const e = memberMap.get(m.user_id);
      if (e) { processed.add(m.user_id); e.balance = e.spent - e.shouldPay; orderedBalances.push({ ...e }); }
    });
    memberMap.forEach((entry, uid) => { if (!processed.has(uid)) { entry.balance = entry.spent - entry.shouldPay; orderedBalances.push({ ...entry }); } });

    const participantSet = new Set<string>();
    approvedReceipts.forEach((r: any) => {
      const uploaderId = resolveUploaderId(r); if (uploaderId) participantSet.add(uploaderId);
      (r.members || []).forEach((p: any) => { if (p && p.user_id) participantSet.add(p.user_id); });
    });

    const settlements = Array.from(settlementMap.values()).filter((s: any) => s.amount > 0.01).sort((a: any, b: any) => b.amount - a.amount);

    const averagePerPerson = participantSet.size > 0 ? totalApproved / participantSet.size : 0;

    return {
      totalApproved,
      totalPending,
      approvedCount: approvedReceipts.length,
      pendingCount: pendingReceipts.length,
      averagePerPerson,
      memberBalances: orderedBalances,
      settlements,
    } as any;
  }, [privateReceiptsForUser, currentTravel, resolveReceiptAmount, resolveUploaderId]);

  const statistics = isPrivateTab ? privateStatistics : publicStatistics;

  // 模拟成员余额数据（根据 APP 截图：只有 Raymond 付了款）
  const memberBalances: MemberBalance[] = useMemo(() => {
    if (!currentTravel?.members || !user) return [];
    
    return currentTravel.members.map((member: any) => {
      const displayName = member.profile?.display_name || '未知用户';
      // 只有 Raymond 付了款 HK$185.53
      const isRaymond = displayName === 'Raymond';
      const spent = isRaymond ? 185.53 : 0;
      const shouldPay = 61.84; // 3人平分
      const balance = spent - shouldPay;
      
      return {
        userId: member.user_id,
        displayName,
        avatarUrl: member.profile?.avatar_url || null,
        spent,
        shouldPay,
        balance,
        share: null,
      };
    });
  }, [currentTravel, user]);

  // 模拟私账结算数据
  const privateSettlements: PrivateSettlement[] = useMemo(() => {
    return [];
  }, []);

  // 生成类别统计数据（基于 activeReceipts，与移动端实现保持一致）
  const categoryStats: CategoryStat[] = useMemo(() => {
    if (!activeReceipts || activeReceipts.length === 0) return [];

    const approvedReceipts = activeReceipts.filter((receipt: any) => {
      if (isPrivateTab) {
        return !receipt.status || receipt.status === 'approved';
      }
      return receipt.status === 'approved';
    });

    if (approvedReceipts.length === 0) return [];

    const totalAmount = (statistics && (statistics.totalApproved ?? 0)) || approvedReceipts.reduce((s: number, r: any) => s + resolveReceiptAmount(r), 0);

    const categoryTotals: Record<string, number> = {};
    approvedReceipts.forEach((receipt: any) => {
      const key = (receipt.category || 'general') as ExpenseCategoryKey | string;
      const amt = resolveReceiptAmount(receipt);
      categoryTotals[key] = (categoryTotals[key] || 0) + amt;
    });

    return Object.entries(categoryTotals).map(([key, amount]) => {
      const label = tExpense(`categories.${key}`) || (tExpense('categories.general') || key);
      const cat = (EXPENSE_CATEGORIES as any)[key] || EXPENSE_CATEGORIES.general;
      return {
        category: label,
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        color: cat.color,
        icon: cat.icon,
      } as CategoryStat;
    }).sort((a, b) => b.amount - a.amount);
  }, [activeReceipts, isPrivateTab, statistics, resolveReceiptAmount, tExpense]);

  // 费用管理回调函数
  const handleUploadReceipt = useCallback(() => {
    // On web navigate to the dedicated upload page (hash-based routing)
    if (typeof window !== 'undefined') {
      console.log('[TravelDetail] handleUploadReceipt called - navigating to upload page');
      window.location.hash = `#travel?view=upload&groupId=${groupId}`;
      return;
    }
    Alert.alert('提示', '上传收据功能请在移动端使用');
  }, [groupId]);

  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      // TODO: 实现 PDF 导出
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (typeof window !== 'undefined') {
        Alert.alert('提示', 'PDF 导出功能开发中');
      }
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleApprove = useCallback(async (receiptId: string) => {
    try {
      await dispatch(approveReceipt({ receipt_id: receiptId, action: 'approve' })).unwrap();
      // refresh receipts
      dispatch(fetchGroupReceipts(groupId));
    } catch (e) {
      console.error('approve failed', e);
    }
  }, [dispatch, groupId]);

  const handleRequestReject = useCallback((receiptId: string) => {
    setRejectingReceiptId(receiptId);
    setRejectReason('');
    setShowRejectModal(true);
  }, []);

  const handleConfirmReject = useCallback(async () => {
    if (!rejectingReceiptId) return;
    try {
      await dispatch(approveReceipt({ receipt_id: rejectingReceiptId, action: 'reject', comment: rejectReason || undefined })).unwrap();
      setShowRejectModal(false);
      setRejectingReceiptId(null);
      setRejectReason('');
      dispatch(fetchGroupReceipts(groupId));
    } catch (e) {
      console.error('reject failed', e);
    }
  }, [dispatch, rejectingReceiptId, rejectReason, groupId]);

  const handleOpenSettings = useCallback(() => {
    if (typeof onOpenExpenseSettings === 'function') {
      try {
        onOpenExpenseSettings();
        return;
      } catch (e) {
        // ignore
      }
    }

    if (typeof window !== 'undefined') {
      Alert.alert('提示', '费用设置功能请在移动端使用');
    }
  }, [onOpenExpenseSettings]);

  const handleAmountPress = useCallback((fullAmount: string) => {
    if (typeof window !== 'undefined') {
      Alert.alert('完整金额', fullAmount);
    }
  }, []);

  // 加载状态
  if (loading && !currentTravel) {
    return (
      <View style={styles.container}>
        <View style={styles.mainContainer}>
          <ModuleHeader
            moduleType="travel"
            title="旅行详情"
            subtitle="查看旅行项目详细信息"
            showMiniCube
            topInset={0}
            showBackButton
            onBack={onBack}
          />
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={MODULE_COLORS.travel} />
            <Text style={styles.loadingText}>正在加载旅行详情...</Text>
          </View>
        </View>
      </View>
    );
  }

  // 错误状态
  if (error && !currentTravel) {
    return (
      <View style={styles.container}>
        <View style={styles.mainContainer}>
          <ModuleHeader
            moduleType="travel"
            title="旅行详情"
            subtitle="查看旅行项目详细信息"
            showMiniCube
            showBackButton
            onBack={onBack}
          />
          <View style={styles.moduleSeparator} />
          <View style={styles.errorContainer}>
            <Icon name="error-outline" size={60} color="#ccc" />
            <Text style={styles.errorText}>加载失败</Text>
            <Text style={styles.errorSubtext}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={loadTravelDetail}>
              <Text style={styles.retryButtonText}>重试</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  if (!currentTravel) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.mainContainer}>
        <ModuleHeader
          moduleType="travel"
          title={currentTravel.name || t('travelDetail.header.title') || '旅行详情'}
          subtitle={currentTravel.location || t('travelDetail.header.subtitle') || '查看旅行项目详细信息'}
          showMiniCube
          topInset={0}
          showBackButton
          onBack={onBack}
        />

        <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[MODULE_COLORS.travel]}
            tintColor={MODULE_COLORS.travel}
          />
        }
      >
        <View style={styles.content}>
          <View style={styles.form}>
          {/* 基本信息卡片 */}
          <View style={styles.cardWrapper}>
            <TravelCard
            travel={({
              id: currentTravel.id,
              name: currentTravel.name,
              location: currentTravel.location || '待定目的地',
              background_image_url: currentTravel.background_image_url || undefined,
              status: currentTravel.status || 'planning',
              start_date: currentTravel.start_date || undefined,
              end_date: currentTravel.end_date || undefined,
              budget_per_person: currentTravel.budget_per_person || undefined,
              payment_mode: currentTravel.payment_mode || undefined,
              currency: currentTravel.currency || undefined,
              visibility: currentTravel.visibility || undefined,
              members: currentTravel.members?.map((member) => ({
                user_id: member.user_id,
                profile: member.profile
                  ? {
                      display_name: member.profile.display_name || undefined,
                      avatar_url: member.profile.avatar_url || undefined,
                    }
                  : undefined,
              })),
            } as any)}
            onChatPress={handleChatPress}
            onChatPress={handleChatPress}
            renderChatButton={({ onPress }) => (
              <ChatPillWeb onPress={onPress} t={t} />
            )}
            t={t}
          />

          {/* 项目简介 / 行程 */}
          {(displayItinerary || isCreator || isMember) && (
            <>
              <View style={styles.flatModule}>
                <ProjectDescription
                  description={displayItinerary}
                  canEdit={isCreator || isMember}
                  onEdit={handleEditItinerary}
                  t={t}
                  initialExpanded={false}
                  maxLines={4}
                  style={styles.projectDescriptionInline}
                />
              </View>
            </>
          )}

          {/* 待办事项 */}
      <View style={styles.cardWrapper}>
        <TodoManagement
        todos={localTodos || []}
        canAddTodo={isCreator || isMember}
        isLoading={loading} // 传递加载状态
        showCard={false}
              onAddTodo={() => {
                console.log('[TravelDetail] onAddTodo called (web)');
                if (Platform.OS === 'web') {
                  console.log('[TravelDetail] opening AddTodoModal (setting showAddTodoModal=true)');
                  setShowAddTodoModal(true);
                  return;
                }
                if (typeof window !== 'undefined') {
                  Alert.alert('提示', '待办事项功能请在移动端使用');
                }
              }}
              onToggleTodo={async (todoId, completed) => {
                if (!todoId) return;
                // optimistic update locally so UI responds immediately
                const prev = localTodos || [];
                const updated = prev.map((t: any) => (t.id === todoId ? { ...t, completed: !completed } : t));
                setLocalTodos(updated);
                try {
                  await dispatch(
                    updateGroupTodo({ id: todoId, updates: { completed: !completed } } as any)
                  ).unwrap();
                  // refresh authoritative list in background
                  try { await dispatch(getTravelTodos(groupId)).unwrap(); } catch (e) { /* ignore */ }
                } catch (e) {
                  console.error('Failed to toggle todo:', e);
                  // revert local optimistic change
                  setLocalTodos(prev);
                  if (typeof window !== 'undefined') {
                    Alert.alert('错误', '无法更新待办状态，请重试');
                  }
                }
              }}
              onAdClick={async (adUrl, adPartner, todoId) => {
                console.log('[TravelDetail] 广告点击:', { adUrl, adPartner, todoId });
                
                // 记录点击事件到数据库
                try {
                  await supabase.from('todo_ad_clicks').insert({
                    todo_id: todoId,
                    user_id: user?.id,
                    ad_partner: adPartner,
                    ad_campaign: localTodos.find((t: any) => t.id === todoId)?.metadata?.adCampaign,
                  });
                } catch (error) {
                  console.warn('记录广告点击失败:', error);
                }
                
                // 打开广告链接
                if (typeof window !== 'undefined') {
                  window.open(adUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              t={t}
        />
      </View>

          {/* 成员列表 */}
          {currentTravel.members && currentTravel.members.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ marginRight: 8 }}>
                    <Icon name="people" size={18} color={MODULE_COLORS.travel} />
                  </View>
                  <Text style={styles.sectionTitle}>{t('travelDetail.content.members') || '成员'}</Text>
                </View>
                {(isCreator || (currentTravel.visibility === 'public' && isMember)) && (
                  <PillButton
                    iconName="person-add"
                    text={isCreator ? '邀请好友' : '推荐好友'}
                    onPress={handleInviteFriends}
                  />
                )}
              </View>

              <View style={styles.membersGrid}>
                {currentTravel.members.map((member, index) => {
                  console.log(`[成员 ${index}]`, {
                    user_id: member.user_id,
                    displayName: member.profile?.display_name,
                    avatarUrl: member.profile?.avatar_url,
                    isCreator: member.user_id === currentTravel.creator_id,
                  });
                  return (
                  <View key={member.user_id || index} style={styles.memberGridItem}>
                    <View style={styles.memberAvatarContainer}>
                      <Avatar
                        size="medium"
                        avatarUrl={member.profile?.avatar_url}
                        displayName={member.profile?.display_name}
                        email={member.user_id}
                        style={styles.memberGridAvatar}
                      />
                      {member.user_id === currentTravel.creator_id && (
                        <View style={styles.creatorBadge}>
                          <Icon name="star" size={12} color="#FFD700" />
                        </View>
                      )}
                    </View>
                    <Text style={styles.memberGridName} numberOfLines={1}>
                      {member.profile?.display_name || t('travelDetail.content.unknownUser') || '未知用户'}
                    </Text>
                    <Text style={styles.memberGridRole}>
                      {member.user_id === currentTravel.creator_id 
                        ? (t('travelDetail.content.creator') || '创建者')
                        : (t('travelDetail.content.member') || '拼客')}
                    </Text>
                  </View>
                  );
                })}
              </View>
            </View>
          )}

          {/* 费用管理 - 使用原子化组件 */}
          <View style={styles.expenseSection}>
            {/* 账本切换标签 */}
            <LedgerTabs
              activeTab={activeLedgerTab}
              onTabChange={setActiveLedgerTab}
              publicCount={receipts.length}
              privateCount={privateReceiptsForUser.length}
              privateLoading={privateReceiptsLoading}
              t={tExpense}
            />
          </View>

            <ExpenseStatisticsCard
              statistics={statistics}
              currency={currentTravel?.currency}
              paymentMode={{
                mode: 'actual_expense',
                useShares: false,
              }}
              isPrivateMode={isPrivateTab}
              isCreator={isCreator}
              formatCompact={formatCurrencyCompact}
              formatFull={formatCurrency}
              onAmountPress={handleAmountPress}
              onOpenSettings={handleOpenSettings}
              t={tExpense}
            />

            {/* 成员余额或私账往来 */}
            {isPrivateTab ? (
              <>
                <View style={{ paddingHorizontal: 12, marginBottom: 8 }}>
                  {privateReceiptsLoading ? (
                    <Text style={{ color: '#64748b' }}>Loading private receipts...</Text>
                  ) : privateReceiptsError ? (
                    <Text style={{ color: '#ef4444' }}>{String(privateReceiptsError)}</Text>
                  ) : (
                                null
                              )}
                 
                            </View>
                <PrivateSettlementList
                  settlements={statistics.settlements || []}
                  currentUserId={user?.id}
                  currency={currentTravel?.currency}
                  hasOtherRecords={Boolean(privateReceipts.length > 0 && privateReceiptsForUser.length === 0)}
                  formatCurrency={formatCurrency}
                />
              </>
            ) : (
              <MemberBalanceList
                balances={statistics.memberBalances || []}
                currency={currentTravel?.currency}
                showShareInfo={false}
                formatCurrency={formatCurrency}
                t={tExpense}
              />
            )}

            {/* Receipts list (web) - moved above Category Statistics */}
            <View style={{ marginTop: 12 }}>
              <Card style={styles.receiptsCard} variant="default">
                <View style={styles.receiptsHeaderRow}>
                  <View style={styles.receiptsHeaderLeft}>
                    <View style={styles.receiptsHeaderIcon}>
                      <Icon name="receipt" size={20} color={MODULE_COLORS.travel} />
                    </View>
                    <Text style={styles.receiptsTitle}>{tExpense('receipts.title') || '收據'}</Text>
                  </View>
                  <UploadReceiptButton
                    onUpload={handleUploadReceipt}
                    onExportPDF={activeLedgerTab === 'public' ? handleExportPDF : undefined}
                    isExporting={isExporting}
                    isPrivateMode={activeLedgerTab === 'private'}
                    t={tExpense}
                  />
                </View>

                {activeReceipts && activeReceipts.length > 0 ? (
                  <View>
                    {activeReceipts.map((r: any) => (
                      <View key={r.id} style={{ marginBottom: 12 }}>
                        <ReceiptSectionWeb
                          receipt={{
                            id: r.id,
                            title: r.title || r.name || 'Receipt',
                            amount: r.amount || 0,
                            currency: r.currency || currentTravel?.currency || '',
                            exchangeRate: r.exchange_rate || undefined,
                            members: (r as any).members || [],
                            // normalize uploader shape for ReceiptCard
                            uploader: {
                              id: (r.uploader && r.uploader.id) || r.uploader_id || null,
                              name: (r.uploader && (r.uploader.display_name || r.uploader.name)) || r.uploader_name || (r.uploader && r.uploader.display_name) || 'You',
                              avatar: (r.uploader && (r.uploader.avatar_url || r.uploader.avatar)) || r.uploader_avatar || (r.uploader && (r.uploader.profile?.avatar_url as any)) || null,
                            },
                            uploadDate: r.created_at || r.updated_at || r.expense_date || null,
                            status: r.status || 'pending',
                            receiptUrl: r.receipt_url || null,
                          }}
                          onUpload={handleUploadReceipt}
                          onExportPDF={handleExportPDF}
                          isExporting={isExporting}
                          t={tExpense}
                          showHeaderActions={false}
                          formatCurrency={formatCurrency}
                          onApprove={() => handleApprove(r.id)}
                          onRequestReject={() => handleRequestReject(r.id)}
                        />
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.receiptsEmptyBody}>
                    <Icon name="receipt" size={48} color="#d1d5db" />
                    <Text style={styles.emptyTitle}>{tExpense('receipts.emptyTitle') || 'No receipts'}</Text>
                    <Text style={styles.emptySubtitle}>{tExpense('receipts.emptySubtitle') || 'Click upload button to start adding receipts'}</Text>
                  </View>
                )}
              </Card>
            </View>

            {/* 类别统计图表 */}
            {categoryStats.length > 0 && (
              <ExpenseCategoryChart
                categories={categoryStats}
                currency={currentTravel?.currency}
                formatCurrency={formatCurrency}
                t={tExpense}
                maxCategories={5}
              />
            )}
          </View>
          </View>
        </View>
      </ScrollView>

      {/* Add Todo modal for web */}
      {showAddTodoModal && (
        <AddTodoModal
          visible={showAddTodoModal}
          isSubmitting={isAddingTodo}
          renderAsInline
          onClose={() => setShowAddTodoModal(false)}
          onAddTodo={async (title, description, priority) => {
            if (!user || !user.id) {
              if (typeof window !== 'undefined') Alert.alert('错误', '请先登录');
              return;
            }
            setIsAddingTodo(true);
            try {
              await dispatch(
                createGroupTodo({
                  title,
                  description,
                  completed: false,
                  category: 'general',
                  priority: (priority as any) || 'medium',
                  groupId: groupId,
                  createdBy: user.id,
                } as any),
              ).unwrap();

              // refresh todos shown in this view
              try {
                await dispatch(getTravelTodos(groupId)).unwrap();
              } catch (e) {
                console.warn('Failed to refresh travel todos after create:', e);
              }

              if (typeof window !== 'undefined') {
                Alert.alert('提示', '已添加待办事项');
              }
            } catch (e: any) {
              console.error('Create todo failed:', e);
              if (typeof window !== 'undefined') {
                Alert.alert('错误', e?.message || '添加待办事项失败');
              }
            } finally {
              setIsAddingTodo(false);
              setShowAddTodoModal(false);
            }
          }}
        />
      )}

      {/* Reject reason modal (inline simple implementation for web) */}
      {showRejectModal && (
        <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <Card style={{ width: 400 }}>
          <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8 }}>拒绝理由</Text>
          <TextInput
            value={rejectReason}
            onChangeText={setRejectReason}
            placeholder="输入拒绝理由（可选）"
            style={{ borderWidth: 1, borderColor: '#e5e7eb', padding: 8, borderRadius: 8, marginBottom: 12 }}
            multiline
          />
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
            <TouchableOpacity onPress={() => setShowRejectModal(false)} style={{ paddingVertical: 8, paddingHorizontal: 12 }}>
              <Text>取消</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleConfirmReject} style={{ backgroundColor: '#ef4444', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6 }}>
              <Text style={{ color: '#fff' }}>确认拒绝</Text>
            </TouchableOpacity>
          </View>
          </Card>
        </View>
      )}

      {/* Upload opens a standalone web page via hash routing (handled by TravelModule) */}
      {/* Upload opens a standalone web page via hash routing (handled by TravelModule) */}
      </View>
    </View>
  );
};const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
      default: {
        elevation: 4,
      }
    }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 32,
  },
  content: {
    flex: 1,
  },
  form: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 8,
  },
  retryButton: {
    marginTop: 24,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: MODULE_COLORS.travel,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  section: {
    ...Platform.select({
      web: {
        backgroundColor: '#f8f9fa',
        marginBottom: 16,
        padding: 16,
        borderRadius: 12,
        boxShadow: 'none',
      },
      default: {
        backgroundColor: '#f8f9fa',
        marginBottom: 16,
        padding: 16,
        borderRadius: 12,
        shadowOpacity: 0,
        elevation: 0,
      },
    }),
  },
  receiptsEmptyCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  receiptsCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  receiptsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  receiptsHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  receiptsHeaderIcon: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  receiptsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  receiptsEmptyBody: {
    alignItems: 'center',
    padding: 20,
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  emptySubtitle: {
    marginTop: 8,
    fontSize: 13,
    color: '#9ca3af',
    textAlign: 'center',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
  },
  editButtonText: {
    marginLeft: 4,
    fontSize: 12,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
  },
  descriptionContainer: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    color: '#374151',
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 8,
  },
  expandButtonText: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
    marginRight: 4,
  },
  emptyState: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6B7280',
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
    textAlign: 'center',
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingVertical: 8,
  },
  memberGridItem: {
    width: '30%',
    minWidth: 80,
    alignItems: 'center',
    paddingVertical: 12,
  },
  memberAvatarContainer: {
    position: 'relative',
    marginBottom: 8,
  },
  memberGridAvatar: {
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      },
      default: {
        shadowColor: MODULE_COLORS.travel,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  creatorBadge: {
    ...Platform.select({
      web: {
        position: 'absolute',
        top: -2,
        right: -2,
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      },
      default: {
        position: 'absolute',
        top: -2,
        right: -2,
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 2,
      },
    }),
  },
  memberGridName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1f2937',
    textAlign: 'center',
    marginBottom: 2,
  },
  memberGridRole: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '500',
    textAlign: 'center',
  },
  expenseSection: {
    marginTop: 0,
  },
  expenseActions: {
    ...Platform.select({
      web: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  moduleSpacing: {
    marginTop: 20,
    marginBottom: 20,
  },
  moduleSeparator: {
    height: 12,
    backgroundColor: '#F3F4F6',
    width: '100%',
  },
  cardWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    ...Platform.select({
      web: { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
      default: { elevation: 1 },
    }),
  },
  flatModule: {
    // visually similar to card but used as a standalone module
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    ...Platform.select({
      web: { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
      default: { elevation: 1 },
    }),
  },
  projectDescriptionInline: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    margin: 0,
    ...Platform.select({
      web: { boxShadow: 'none' },
      default: { elevation: 0 },
    }),
  },
});

export default TravelDetail;